import { ApplicationSelectData, ApplicationType, AuthenticatedContext } from '@sec-loans-ui/common/types'
import { WizardContext as IfpLoansWizardContext } from '@sec-loans-ui/ifp-loans/wizard/wizardMachine'
import { WizardContext as LoanOfferWizardContext } from '@sec-loans-ui/loan-offer/wizard/wizardMachine'
import {
  getIfpLoanFromUserByApplicationId,
  getIfpLoanFromUserByCreditId,
  getSecLoansEntry,
} from '@sec-loans-ui/utils/helpers'
import { User } from '@sec-loans-ui/wizard/types'
import {
  initialSecLoansWizardContext,
  WizardContext as SecLoansWizardContext,
} from '@sec-loans-ui/wizard/wizardMachine'
import { assertValue } from '@shared/helpers'
import { SecLoansCreateApplicationResponse, SecLoansEntry, SecLoansParam } from '@shared/sec-loans'
import { ConfigResponse } from '@shared/types'
import { AuthenticatedEvent, simpleStepMachine } from '@ui-common/state'
import { AuthenticationCancelledEvent } from '@ui-common/steps/AuthenticateStep/types'
import { GetDataDoneEvent } from '@ui-common/steps/GetDataStep/getDataMachine'
import { ExitWizardEvent, NextStepEvent as CommonNextStepEvent } from '@ui-common/types'
import { isComingBackFromAuthentication } from '@ui-common/utils/authentication'
import { toNumber } from '@ui-common/utils/helpers'
import { assign, setup, StateFrom } from 'xstate'

import { OnCreatedIfpApplicationData } from '../../ifp-loans/types'
import { resolveInitialLoanOfferId } from '../../loan-offer/helpers'

export interface WizardContext extends Partial<AuthenticatedContext>, ConfigResponse<SecLoansParam> {
  user?: User
  shouldLogoutAfterAuthentication?: boolean
  ifpLoansEntryContext?: Partial<IfpLoansWizardContext>
  loanOfferEntryContext?: Partial<LoanOfferWizardContext>
  secLoansEntryContext?: Partial<SecLoansWizardContext>
}

export enum WizardStep {
  Initialize = 'StepInitialize',
  Entrance = 'StepEntrance',
  Authenticate = 'StepAuthenticate',
  GetUserInfo = 'StepGetUserInfo',
  LoanCatalog = 'StepLoanCatalog',
  Assistance = 'StepAssistance',
  GoToIfpLoans = 'StepGoToIfpLoans',
  GoToLoanOffer = 'StepGoToLoanOffer',
  GoToSecLoans = 'StepGoToSecLoans',
  Aborted = 'StepAborted',
}

const initialContext: WizardContext = {
  secLoansEntryContext: initialSecLoansWizardContext,
}

export const shouldHydrateState = (state: StateFrom<WizardMachineType>): boolean => {
  return (
    (state?.value === WizardStep.Authenticate && isComingBackFromAuthentication()) ||
    [WizardStep.GoToIfpLoans, WizardStep.GoToSecLoans, WizardStep.LoanCatalog].includes(state?.value)
  )
}

export type SelectAppEvent = {
  type: 'SELECT_APP'
} & ApplicationSelectData

export type CreatedSecLoansEvent = {
  type: 'CREATED_SEC_LOANS_APPLICATION'
  data: SecLoansCreateApplicationResponse
}

export type CreatedIfpLoansEvent = {
  type: 'CREATED_IFP_LOANS_APPLICATION'
  data: OnCreatedIfpApplicationData
}

type GoToCatalogEvent = { type: 'GO_TO_CATALOG' }
type GoToAssistanceEvent = { type: 'GO_TO_ASSISTANCE' }
type NextStepEvent = CommonNextStepEvent<unknown>
type WizardEvent =
  | GoToCatalogEvent
  | GoToAssistanceEvent
  | SelectAppEvent
  | CreatedSecLoansEvent
  | CreatedIfpLoansEvent
  | NextStepEvent
  | AuthenticatedEvent
  | AuthenticationCancelledEvent
  | GetDataDoneEvent<User>
  | ExitWizardEvent

export type WizardMachineType = ReturnType<typeof createWizardMachine>
// return type is derived from setup fn and used in WizardMachineType
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const createWizardMachine = (authenticatedContext?: AuthenticatedContext) =>
  setup({
    types: {
      context: {} as WizardContext,
      events: {} as WizardEvent,
    },
    actions: {
      saveInitialData: assign(({ context }, config: ConfigResponse<SecLoansParam>) => {
        const minimumHousingLoanAmount = toNumber(config.params?.MINIMUM_HOUSING_LOAN_AMOUNT)
        const minimumPurchaseLoanAmount = toNumber(config.params?.MINIMUM_PURCHASE_LOAN_AMOUNT)
        return {
          ...config,
          ifpLoansEntryContext: {
            ...context.ifpLoansEntryContext,
            ...config,
          },
          secLoansEntryContext: {
            ...context.secLoansEntryContext,
            ...config,
            validationParams: {
              minimumHousingLoanAmount: assertValue(minimumHousingLoanAmount, 'minimumHousingLoanAmount'),
              minimumPurchaseLoanAmount: assertValue(minimumPurchaseLoanAmount, 'minimumPurchaseLoanAmount'),
            },
          },
        }
      }),
      saveAuthenticationData: assign(({ context }, authToken: string) => {
        return {
          authToken,
          ifpLoansEntryContext: {
            ...context.ifpLoansEntryContext,
            authToken,
          },
          secLoansEntryContext: {
            ...context.secLoansEntryContext,
            authToken,
          },
        }
      }),
      storeBasicInfo: assign(({ context }, user: User) => {
        return {
          user,
          ifpLoansEntryContext: {
            ...context.ifpLoansEntryContext,
            user,
          },
          loanOfferEntryContext: {
            ...context.loanOfferEntryContext,
            loanOffer: user.loanOffers.find((loanOffer) => loanOffer.id === resolveInitialLoanOfferId(user)),
            user,
          },
          secLoansEntryContext: {
            ...context.secLoansEntryContext,
            user,
          },
        }
      }),
      storeCreatedSecLoansApplication: assign(({ context }, data: SecLoansCreateApplicationResponse) => {
        return {
          secLoansEntryContext: {
            ...context.secLoansEntryContext,
            isNewApplication: true,
            ...data,
            applicationMetadata: { ...data },
            applicationData: data,
            catalogStepData: {
              applicationType: data.applicationType,
              applicationPurpose: data.applicationPurpose,
              applicationId: data.applicationId,
            },
          },
        }
      }),
      storeCreatedIfpApplication: assign(({ context }, { applicationId, creditId }: OnCreatedIfpApplicationData) => {
        return {
          ifpLoansEntryContext: {
            ...context.ifpLoansEntryContext,
            isNewApplication: true,
            applicationId,
            loan: getIfpLoanFromUserByCreditId(assertValue(context.user, 'context.user'), creditId),
          },
        }
      }),
      storeApplicationSelection: assign(({ context }, event: SelectAppEvent) => {
        return {
          ifpLoansEntryContext:
            event.applicationType === ApplicationType.InstalmentFreePeriodApplication
              ? ({
                  ...context.ifpLoansEntryContext,
                  applicationId: event.applicationId,
                  loan: getIfpLoanFromUserByApplicationId(
                    assertValue(context.user, 'context.user'),
                    event.applicationId,
                  ),
                  isNewApplication: false,
                } satisfies Partial<IfpLoansWizardContext>)
              : undefined,
          secLoansEntryContext:
            event.applicationType === ApplicationType.LoanApplication
              ? ({
                  ...context.secLoansEntryContext,
                  ...event.data, // this is needed cuz sec-loans expects SecLoansApplicationMetadata in context root as well as in catalogStepData
                  catalogStepData: {
                    applicationId: event.applicationId,
                    ...event.data,
                  },
                  isNewApplication: false,
                } satisfies Partial<SecLoansWizardContext>)
              : undefined,
        }
      }),
    },
    guards: {
      shouldGoLoanOffer: ({ context }, data: User) => {
        if (context.params?.IS_LOAN_OFFER_ENABLED !== 'true') {
          return false
        }
        const validEntry = [SecLoansEntry.Default, SecLoansEntry.LoanOffer].includes(getSecLoansEntry())
        return validEntry && !!resolveInitialLoanOfferId(data)
      },
      shouldGoToCatalog: ({ context }, data: User) =>
        (context.params?.IS_IFP_ENABLED === 'true' && getSecLoansEntry() === SecLoansEntry.Catalog) ||
        (context.params?.IS_LOAN_OFFER_ENABLED === 'true' && data.loanOffers.length > 1),
      shouldGoToAssistance: ({ context }) => {
        const isIfpLoansEnabled = context.params?.IS_IFP_ENABLED === 'true'
        const entry = getSecLoansEntry()
        const assistantFlows = [
          SecLoansEntry.AssistanceIfpLoans,
          SecLoansEntry.AssistanceSecLoans,
          SecLoansEntry.Default,
        ]
        return isIfpLoansEnabled && assistantFlows.includes(entry)
      },
      isSelectedAppSecLoans: (_, applicationType: ApplicationType) =>
        applicationType === ApplicationType.LoanApplication,
      isSelectedAppIfpLoans: (_, applicationType: ApplicationType) =>
        applicationType === ApplicationType.InstalmentFreePeriodApplication,
      shouldPassCbsToken: ({ context }) => {
        return !!context.exitUrls?.cbsTokenParameter
      },
      shouldLogoutAfterAuthentication: ({ context }) => {
        return !!context.shouldLogoutAfterAuthentication
      },
    },
    actors: {
      simpleStepMachine,
    },
  }).createMachine({
    id: 'rootWizardMachine',
    initial: authenticatedContext ? WizardStep.GetUserInfo : WizardStep.Initialize,
    context: authenticatedContext ? { ...initialContext, ...authenticatedContext } : initialContext,
    on: { EXIT_WIZARD: { target: `.${WizardStep.Aborted}` } },
    states: {
      [WizardStep.Initialize]: {
        id: WizardStep.Initialize,
        on: {
          NEXT_STEP: {
            target: WizardStep.Entrance,
            actions: {
              type: 'saveInitialData',
              params: ({ event }) => (event as CommonNextStepEvent<ConfigResponse<SecLoansParam>>).data,
            },
          },
        },
      },
      [WizardStep.Entrance]: {
        id: WizardStep.Entrance,
        invoke: {
          id: 'entranceStepMachine',
          src: 'simpleStepMachine',
        },
        on: {
          NEXT_STEP: { target: WizardStep.Authenticate },
          EXIT_WIZARD: [
            {
              guard: { type: 'shouldPassCbsToken' },
              target: WizardStep.Authenticate,
              actions: assign(() => ({
                shouldLogoutAfterAuthentication: true,
              })),
            },
            {
              target: WizardStep.Aborted,
            },
          ],
        },
      },
      [WizardStep.Authenticate]: {
        id: WizardStep.Authenticate,
        on: {
          AUTHENTICATION_DONE: [
            {
              guard: { type: 'shouldLogoutAfterAuthentication' },
              target: WizardStep.Aborted,
              actions: { type: 'saveAuthenticationData', params: ({ event }) => event.data.authToken },
            },
            {
              target: WizardStep.GetUserInfo,
              actions: { type: 'saveAuthenticationData', params: ({ event }) => event.data.authToken },
            },
          ],
          AUTHENTICATION_CANCELLED: {
            target: WizardStep.Entrance,
          },
        },
      },
      [WizardStep.GetUserInfo]: {
        id: WizardStep.GetUserInfo,
        on: {
          GET_DATA_DONE: [
            {
              target: WizardStep.GoToLoanOffer,
              guard: { type: 'shouldGoLoanOffer', params: ({ event }) => event.data },
              actions: {
                type: 'storeBasicInfo',
                params: ({ event }) => event.data,
              },
            },
            {
              target: WizardStep.LoanCatalog,
              guard: { type: 'shouldGoToCatalog', params: ({ event }) => event.data },
              actions: {
                type: 'storeBasicInfo',
                params: ({ event }) => event.data,
              },
            },
            {
              target: WizardStep.Assistance,
              guard: { type: 'shouldGoToAssistance' },
              actions: {
                type: 'storeBasicInfo',
                params: ({ event }) => event.data,
              },
            },
            {
              target: WizardStep.GoToSecLoans,
              actions: {
                type: 'storeBasicInfo',
                params: ({ event }) => event.data,
              },
            },
          ],
        },
      },
      [WizardStep.LoanCatalog]: {
        id: WizardStep.LoanCatalog,
        on: {
          GO_TO_ASSISTANCE: {
            target: WizardStep.Assistance,
          },
          SELECT_APP: [
            {
              guard: { type: 'isSelectedAppSecLoans', params: ({ event }) => event.applicationType },
              target: WizardStep.GoToSecLoans,
              actions: {
                type: 'storeApplicationSelection',
                params: ({ event }) => assertValue(event, 'event'),
              },
            },
            {
              guard: { type: 'isSelectedAppIfpLoans', params: ({ event }) => event.applicationType },
              target: WizardStep.GoToIfpLoans,
              actions: {
                type: 'storeApplicationSelection',
                params: ({ event }) => assertValue(event, 'event'),
              },
            },
          ],
        },
      },
      [WizardStep.Assistance]: {
        id: WizardStep.Assistance,
        on: {
          GO_TO_CATALOG: {
            target: WizardStep.LoanCatalog,
          },
          CREATED_SEC_LOANS_APPLICATION: {
            actions: {
              type: 'storeCreatedSecLoansApplication',
              params: ({ event }) => assertValue(event.data, 'event.data'),
            },
            target: WizardStep.GoToSecLoans,
          },
          CREATED_IFP_LOANS_APPLICATION: {
            actions: {
              type: 'storeCreatedIfpApplication',
              params: ({ event }) => assertValue(event.data, 'event.data'),
            },
            target: WizardStep.GoToIfpLoans,
          },
        },
      },
      [WizardStep.GoToIfpLoans]: {
        id: WizardStep.GoToIfpLoans,
      },
      [WizardStep.GoToLoanOffer]: {
        id: WizardStep.GoToLoanOffer,
      },
      [WizardStep.GoToSecLoans]: {
        id: WizardStep.GoToSecLoans,
      },
      [WizardStep.Aborted]: {
        id: WizardStep.Aborted,
        type: 'final',
      },
    },
  })
